import { useMemo, useState } from "react"
import { ModalBody } from "@mezo-org/mezo-clay"

import { RootState, selectTokenDecimals, useDappSelector } from "#/redux-state"
import SummaryList from "#/shared/components/SummaryList"
import TermsOfUseCheckbox from "#/shared/components/TermsOfUseCheckbox"
import { ETHEREUM_CHAIN_ID, IS_MEZO_MAINNET, vault } from "#/shared/constants"
import { useEstimateReceiptTokenAmount } from "#/shared/hooks"
import { VaultDepositFormValue } from "#/shared/types"
import {
  bigIntToHumanReadableFormat,
  getCurrencySymbol,
  vaultUtils,
} from "#/shared/utils"
import { SwitchChainButton } from "#/ui/Button"
import {
  ComponentStepProps,
  StepperAbsoluteModalFooter,
  StepperModalButton,
  useStepperModalContext,
} from "#/ui/Modal"

export default function VaultReviewStep({
  onClickContinue,
}: ComponentStepProps) {
  const [hasAcceptedTerms, setHasAcceptedTerms] = useState(false)

  const {
    values: { token, amount },
  } = useStepperModalContext<VaultDepositFormValue>()

  const tokenDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, token),
  )

  const receiptTokenAmount = useEstimateReceiptTokenAmount(token, amount)

  const summary = useMemo(
    () => [
      {
        label: "Your depositing",
        endEnhancer: `${bigIntToHumanReadableFormat(
          amount ?? 0n,
          tokenDecimals,
        )} ${getCurrencySymbol(token)}`,
      },
      {
        label: "Your receiving",
        endEnhancer: `${bigIntToHumanReadableFormat(receiptTokenAmount)} ${
          vault.VAULT_DEPOSIT_TOKEN_TO_SHARE_TOKEN_NAME[token]
        }`,
      },
      {
        label: "Avg APY",
        endEnhancer: `${vaultUtils.getAvgAPY()}%`,
      },
    ],
    [amount, receiptTokenAmount, token, tokenDecimals],
  )

  return (
    <>
      <ModalBody>
        <SummaryList list={summary} />
      </ModalBody>
      <StepperAbsoluteModalFooter>
        <TermsOfUseCheckbox
          checked={hasAcceptedTerms}
          onChange={(ev) => setHasAcceptedTerms(ev.target.checked)}
        />
        <SwitchChainButton
          width="100%"
          size="large"
          shape="default"
          label={IS_MEZO_MAINNET ? "Ethereum" : "Sepolia"}
          chainId={ETHEREUM_CHAIN_ID}
          disabled={!hasAcceptedTerms}
        >
          <StepperModalButton
            disabled={!hasAcceptedTerms}
            onClick={onClickContinue}
          >
            Review
          </StepperModalButton>
        </SwitchChainButton>
      </StepperAbsoluteModalFooter>
    </>
  )
}
